var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorAccessBar_1;
/**
 * @packageDocumentation
 * @module      CorAccessBar.ts
 * @description Encapsulates the Shui-compliant Access Bar for this application.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
import { LitElement, html, customElement, css, internalProperty } from 'lit-element';
import './CorThemeSelector';
import LoggedInUser from "./LoggedInUser";
import ValidationTracker from "./ValidationTracker";
import { universalStyles } from "./CorMain";
import PropertyBindingHandler from './PropertyBindingHandler';
import { msLogOut, MSALComponent } from './MsalMain';
/**
 * Encapsulates the Shui-compliant Access Bar for this application.
 * @noInheritDoc
 */
let CorAccessBar = CorAccessBar_1 = class CorAccessBar extends LitElement {
    constructor() {
        super(...arguments);
        this.validationTracker = new ValidationTracker();
        /** True if the Settings popover should show; false if not. */
        this.showSettings = false;
        this.popoverTitle = '';
        this.popoverIsError = false;
        this.popoverText = '';
        this.isShowPopover = false;
    }
    /**
     * LitElement looks for a static property by this name. If it finds one, it applies these styles to the component.
     * See https://lit-element.polymer-project.org/guide/styles#expressions
     */
    static get styles() {
        return css `
          ${universalStyles}

          sh-popover {
            position: relative;
            float: right;
            top: 0;
            right: 20px;
            z-index: 100;
            margin-bottom: -1000px; // Otherwise the nav bar gets pushed down.
          }
                    
          #settings {
            position: absolute;
            top: 70px;
            width: auto;
            height: auto;
          }
          
          #successOrFailure {
            height: 150px;
            width: 330px;
          }

        `;
    }
    hidePopover() {
        this.isShowPopover = false;
    }
    inputHelper(targetProperty) {
        const self = this;
        return (e) => {
            PropertyBindingHandler.bindPropertyWithEventTargetValue(self, targetProperty, e.currentTarget);
        };
    }
    displayPopover(title, isError, text) {
        this.popoverTitle = title;
        this.popoverIsError = isError;
        this.popoverText = text;
        this.isShowPopover = true;
    }
    showSettingsPopover() {
        this.showSettings = true;
    }
    hideSettingsPopover() {
        this.showSettings = false;
    }
    static getLabel() {
        return "Monadnock Systems Dashboard";
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        var _a, _b, _c;
        // Recreate the menu from scratch each time. This prevents Shui from remembering which one was
        // highlighted ("active") when the Settings popup was dismissed and highlighting that one again when the popup
        // is made visible again..
        let settingsPopover = this.showSettings ? html `
            <sh-popover id="settings" target="userIdentifier" position="bottom"
                        slot="functions"
                        ?visible=${this.showSettings}
                        sticky
                        label=${(_a = LoggedInUser.userDetails) === null || _a === void 0 ? void 0 : _a.name}>
                <sh-icon icon="cancel" slot="functions" size="s" button @click="${this.hideSettingsPopover}"></sh-icon>
                <popover-label-additional-line>
                    ${(_b = LoggedInUser.userDetails) === null || _b === void 0 ? void 0 : _b.username}
                </popover-label-additional-line>
                <sh-divider></sh-divider>
                <pseudo-menu-item>
                    <sh-text size="title-1" color="primary">Version</sh-text>
                    <sh-text>${MSALComponent.sessionManagerVersion}</sh-text>
                </pseudo-menu-item>
                <sh-divider></sh-divider>
                <pseudo-menu-item>
                    <cor-theme-selector></cor-theme-selector>
                </pseudo-menu-item>
                </div>
                <sh-menu-item label="Log off" @click=${msLogOut}></sh-menu-item>
            </sh-popover>` : html ``;
        let userDependentElements = LoggedInUser.userDetails !== undefined ? // Should always be true.
            html `
                <sh-user-identifier id="userIdentifier"
                                    name="${(_c = LoggedInUser.userDetails) === null || _c === void 0 ? void 0 : _c.username}"
                                    info="${LoggedInUser.userDetails.name}"
                                    @click=${this.showSettingsPopover}
                                    slot="functions">
                </sh-user-identifier>
                ${settingsPopover}
                ` :
            html ``;
        return html `
            <sh-access-bar id="access" label="${CorAccessBar_1.getLabel()}" logo="./assets/img/SHS_Logo.svg" slot="access">
                ${userDependentElements}
            </sh-access-bar>     

            <sh-popover id="successOrFailure" label="${this.popoverTitle}" passive position="top-right" target="access"
                        ?sticky=${this.popoverIsError}
                        type="${this.popoverIsError ? "error" : "confirmation"}"
                        ?visible=${this.isShowPopover}>
                <sh-icon icon="cancel" size="s" button slot="functions" @click="${this.hidePopover}"></sh-icon>
                <sh-text>${this.popoverText}</sh-text>
            </sh-popover>
        `;
    }
};
__decorate([
    internalProperty()
], CorAccessBar.prototype, "showSettings", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverTitle", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverIsError", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverText", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "isShowPopover", void 0);
CorAccessBar = CorAccessBar_1 = __decorate([
    customElement('cor-access-bar')
], CorAccessBar);
export { CorAccessBar };
//# sourceMappingURL=CorAccessBar.js.map